import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Truck, MapPin, Radio, Search, Crosshair, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Custom SVG-based marker icons so we don't 404 on the default leaflet PNGs
// (they're missing when bundled through webpack). Two variants: standard
// green pin for live vendors, amber for boosted ones.
const makeIcon = (color) => L.divIcon({
  className: "foodtruck-pin",
  html: `<div style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));">
    <svg width="34" height="46" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 0C7.6 0 0 7.6 0 17c0 12.75 17 29 17 29s17-16.25 17-29C34 7.6 26.4 0 17 0z" fill="${color}"/>
      <circle cx="17" cy="17" r="9" fill="#ffffff"/>
      <path d="M11 17h2v-3h6l1.5 2H22v1h-1v3h-1a1.5 1.5 0 01-3 0h-3a1.5 1.5 0 01-3 0h-1v-3z" fill="${color}"/>
    </svg>
  </div>`,
  iconSize: [34, 46],
  iconAnchor: [17, 44],
  popupAnchor: [0, -40],
});
const LIVE_ICON    = makeIcon("#059669");   // emerald
const BOOSTED_ICON = makeIcon("#F59E0B");   // amber
const STREAM_ICON  = makeIcon("#DC2626");   // red — for actively-streaming pins

/**
 * FoodTrucks — public "live map" for GPS check-ins.
 *
 * Every mobile vendor (food truck / pop-up / mobile service) that has pinged
 * their current spot in the last 6 hours appears as a pin on the Leaflet map.
 * Click a pin → popup with details + "Get directions" + "View profile".
 *
 * Streams that were started with a check-in label (see `/marketplace/live/new
 * ?checkin=1`) show a red pin so viewers can jump straight into the broadcast.
 */
export default function FoodTrucks() {
  const [items, setItems] = useState([]);
  const [live, setLive] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("live");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  // Server-side geo/cuisine filters — applied on every fetch (debounced input)
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [me, setMe] = useState({ lat: null, lng: null });

  useEffect(() => {
    let alive = true;
    // Debounce the text filters so we're not thrashing the API on every keystroke
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {
          limit: 200,
          mobile_type: type || undefined,
          city: city.trim() || undefined,
          zip: zip.trim() || undefined,
          cuisine: cuisine.trim() || undefined,
        };
        const [dir, liveOnly, streams] = await Promise.all([
          api.get("/mobile/directory", { params }),
          api.get("/mobile/live", { params: { ...params, limit: 100 } }),
          api.get("/marketplace/live/sessions", { params: { status: "live", limit: 60 } }).catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        setItems(dir.data?.items || []);
        setLive(liveOnly.data?.items || []);
        setLiveStreams((streams.data?.items || []).filter((s) => (s.checkin_label || "").trim().length > 0));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(handle); };
  }, [type, city, zip, cuisine]);

  const detectMe = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Your browser can't share GPS");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setMe({ lat: p.coords.latitude, lng: p.coords.longitude });
        toast.success("Sorted by distance from you");
      },
      (err) => toast.error(`Location denied: ${err.message}`),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  // Only vendors with coords go on the map
  const mapPins = useMemo(() => live.filter((b) => {
    const loc = b.current_location;
    return loc && loc.lat != null && loc.lng != null;
  }), [live]);

  const streamPins = useMemo(() => liveStreams.filter(
    (s) => s.checkin_lat != null && s.checkin_lng != null
  ), [liveStreams]);

  // Center on average of all pins, else user, else continental US
  const mapCenter = useMemo(() => {
    if (mapPins.length > 0) {
      const lat = mapPins.reduce((a, b) => a + b.current_location.lat, 0) / mapPins.length;
      const lng = mapPins.reduce((a, b) => a + b.current_location.lng, 0) / mapPins.length;
      return [lat, lng];
    }
    if (me.lat != null && me.lng != null) return [me.lat, me.lng];
    return [39.8283, -98.5795]; // continental US
  }, [mapPins, me]);
  const mapZoom = mapPins.length === 0 ? 4 : mapPins.length === 1 ? 14 : 11;

  const visible = useMemo(() => {
    const base = tab === "live" ? live : items;
    const needle = q.trim().toLowerCase();
    let arr = base.filter((b) => {
      if (!needle) return true;
      return (
        (b.business_name || "").toLowerCase().includes(needle) ||
        (b.cuisine || "").toLowerCase().includes(needle) ||
        (b.city || "").toLowerCase().includes(needle)
      );
    });
    if (me.lat != null && me.lng != null) {
      arr = arr.map((b) => {
        const loc = b.current_location;
        const d = loc?.lat != null && loc?.lng != null
          ? haversineMi(me.lat, me.lng, loc.lat, loc.lng)
          : Number.POSITIVE_INFINITY;
        return { ...b, _dist: d };
      }).sort((a, b) => a._dist - b._dist);
    }
    return arr;
  }, [items, live, tab, q, me]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10" data-testid="foodtrucks-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-emerald-700 flex items-center gap-2"><Truck size={12} /> Live map · GPS check-ins</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-1">Food trucks & pop-ups</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Every pin is a mobile vendor checked-in within the last 6 hours. Amber pins are boosted, red pins are streaming live right now.
            Own a truck or pop-up? <Link to="/mobile/manage" className="text-[#B31E1E] hover:underline">Manage your check-ins</Link>.
          </p>
        </div>
        <Link to="/mobile/manage">
          <Button className="rounded-[35px] bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="foodtrucks-manage-cta">
            <Truck size={14} className="mr-2" /> I run a truck / pop-up
          </Button>
        </Link>
      </header>

      {/* THE MAP — Leaflet with OpenStreetMap tiles */}
      <Card className="rounded-md mt-6 overflow-hidden relative" data-testid="foodtrucks-map-card">
        <div className="h-[520px] w-full bg-[#e4e0dc]">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            scrollWheelZoom
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {/* Recenter whenever pins or "me" change */}
            <RecenterOnPins pins={mapPins} me={me} />

            {/* Vendor GPS pins */}
            {mapPins.map((b) => {
              const loc = b.current_location;
              const isStreaming = streamPins.some(
                (s) => s.host_id === b.id
              );
              const icon = isStreaming ? STREAM_ICON : loc.boosted ? BOOSTED_ICON : LIVE_ICON;
              const directions = `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`;
              return (
                <Marker key={b.id} position={[loc.lat, loc.lng]} icon={icon}>
                  <Popup>
                    <div className="min-w-[180px]" data-testid={`pin-popup-${b.id}`}>
                      <div className="font-semibold text-sm text-[#1B1614]">{b.business_name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {b.cuisine || (b.mobile_type === "food_truck" ? "Food truck" : b.mobile_type === "popup" ? "Pop-up shop" : "Mobile business")}
                      </div>
                      <div className="mt-2 text-xs text-emerald-800 flex items-start gap-1"><MapPin size={11} className="mt-0.5 shrink-0" /> {loc.label}</div>
                      {loc.pinged_at && <div className="text-[10px] text-gray-500 font-mono mt-1">Pinged {relativeTime(loc.pinged_at)}</div>}
                      {loc.boosted && <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-700 font-semibold"><Sparkles size={9} /> Boosted</div>}
                      {isStreaming && <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-red-600 font-semibold">🔴 Live streaming now</div>}
                      <div className="mt-2 flex gap-1.5">
                        <a href={directions} target="_blank" rel="noopener noreferrer" className="text-[11px] bg-[#B31E1E] text-white rounded px-2 py-1 font-semibold hover:bg-[#8F1717]" data-testid={`pin-directions-${b.id}`}>
                          Directions
                        </a>
                        <Link to={`/business/${b.id}`} className="text-[11px] bg-white border border-gray-300 rounded px-2 py-1 font-semibold hover:border-[#B31E1E]" data-testid={`pin-view-${b.id}`}>
                          Profile
                        </Link>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Standalone stream pins (owner may have gone live from a coord without a full check-in) */}
            {streamPins.filter((s) => !mapPins.some((b) => b.id === s.host_id)).map((s) => (
              <Marker key={`stream-${s.id}`} position={[s.checkin_lat, s.checkin_lng]} icon={STREAM_ICON}>
                <Popup>
                  <div className="min-w-[180px]">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-red-600 font-bold">🔴 Live</div>
                    <div className="font-semibold text-sm text-[#1B1614] mt-1">{s.title}</div>
                    <div className="text-[11px] text-emerald-800 mt-1"><MapPin size={10} className="inline mr-0.5" /> {s.checkin_label}</div>
                    <Link to={`/marketplace/live/${s.id}`} className="mt-2 inline-block text-[11px] bg-red-600 text-white rounded px-2 py-1 font-semibold hover:bg-red-700">Watch live</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        {mapPins.length === 0 && streamPins.length === 0 && !loading && (
          <div className="absolute inset-x-0 bottom-4 mx-auto max-w-md rounded-md bg-white/95 border border-border shadow-lg p-3 text-center text-sm text-gray-700 backdrop-blur-sm">
            <Truck size={16} className="inline mr-1.5 text-emerald-700" />
            No vendors checked in with GPS coordinates yet. Check back at lunch or use the list below.
          </div>
        )}
      </Card>

      {/* Live streams strip (streams without GPS coords still appear here so nobody misses them) */}
      {liveStreams.length > 0 && (
        <Card className="rounded-md mt-6 p-4 bg-red-50 border-red-200" data-testid="foodtrucks-live-streams">
          <div className="flex items-center gap-2 mb-3">
            <Radio size={14} className="text-red-600 animate-pulse" />
            <div className="font-semibold text-sm text-[#1B1614]">Streaming from a truck right now</div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
            {liveStreams.map((s) => (
              <Link key={s.id} to={`/marketplace/live/${s.id}`} className="shrink-0 w-64 rounded-md border border-red-200 bg-white p-3 hover:border-red-500 transition-colors" data-testid={`foodtruck-stream-${s.id}`}>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-red-600 font-bold">Live</span>
                </div>
                <div className="mt-1 font-semibold text-sm truncate">{s.title}</div>
                <div className="text-[11px] text-gray-500 truncate mt-0.5"><MapPin size={9} className="inline mr-0.5" /> {s.checkin_label}</div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* Filters — text search + city / zip / cuisine (server-side) + type + near-me */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="foodtrucks-filters">
        <div className="relative lg:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name…"
            className="rounded-sm pl-9"
            data-testid="foodtrucks-search"
          />
        </div>
        <Input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City"
          className="rounded-sm"
          data-testid="foodtrucks-city"
        />
        <Input
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
          placeholder="ZIP"
          className="rounded-sm font-mono"
          data-testid="foodtrucks-zip"
        />
        <Input
          value={cuisine}
          onChange={(e) => setCuisine(e.target.value)}
          placeholder="Cuisine (Tacos, BBQ, Thai…)"
          className="rounded-sm"
          data-testid="foodtrucks-cuisine"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="rounded-sm border border-border bg-white text-sm px-3 py-2"
          data-testid="foodtrucks-type-filter"
        >
          <option value="">All types</option>
          <option value="food_truck">Food trucks</option>
          <option value="popup">Pop-up shops</option>
          <option value="mobile_service">Mobile services</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          onClick={detectMe}
          className="rounded-[35px]"
          data-testid="foodtrucks-near-me"
        >
          <Crosshair size={13} className="mr-1.5" /> Sort by distance
        </Button>
        {(city || zip || cuisine || type) && (
          <Button
            variant="ghost"
            onClick={() => { setCity(""); setZip(""); setCuisine(""); setType(""); }}
            className="rounded-[35px] text-xs text-gray-600"
            data-testid="foodtrucks-clear-filters"
          >
            Clear filters
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-sm">
          <TabsTrigger value="live" data-testid="foodtrucks-tab-live">Live now ({live.length})</TabsTrigger>
          <TabsTrigger value="all"  data-testid="foodtrucks-tab-all">All vendors ({items.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-sm text-gray-500 mt-6">Loading…</p>
      ) : visible.length === 0 ? (
        <Card className="rounded-md p-10 mt-6 text-center text-gray-600" data-testid="foodtrucks-empty">
          <Truck className="mx-auto mb-2 text-gray-400" size={28} />
          {tab === "live"
            ? "Nobody's checked in right now. Check back at lunch or dinner."
            : "No vendors match those filters yet."}
        </Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="foodtrucks-grid">
          {visible.map((b) => <VendorCard key={b.id} b={b} />)}
        </div>
      )}
    </div>
  );
}

function RecenterOnPins({ pins, me }) {
  const map = useMap();
  useEffect(() => {
    if (pins.length === 0) return;
    if (pins.length === 1) {
      const p = pins[0].current_location;
      map.setView([p.lat, p.lng], 14, { animate: true });
      return;
    }
    // Fit all pins in view
    const bounds = L.latLngBounds(pins.map((b) => [b.current_location.lat, b.current_location.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [pins, map]);
  useEffect(() => {
    if (me.lat != null && me.lng != null && pins.length === 0) {
      map.setView([me.lat, me.lng], 12, { animate: true });
    }
  }, [me, pins.length, map]);
  return null;
}


function VendorCard({ b }) {
  const loc = b.current_location;
  const live = b.is_live_now;
  const boosted = live && loc?.boosted;
  const directions = loc?.lat != null && loc?.lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`
    : null;
  return (
    <Card className={`rounded-md overflow-hidden ${boosted ? "border-amber-400 shadow-[0_10px_30px_-14px_rgba(245,158,11,0.5)]" : live ? "border-emerald-400" : ""}`} data-testid={`vendor-card-${b.id}`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`h-12 w-12 rounded-md flex items-center justify-center shrink-0 ${b.mobile_type === "popup" ? "bg-indigo-50 text-indigo-700" : "bg-emerald-50 text-emerald-700"}`}>
            {b.logo_data_url ? (
              <img src={b.logo_data_url} alt="" className="h-12 w-12 rounded-md object-cover" />
            ) : (
              <Truck size={22} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-semibold text-[#1B1614] truncate">{b.business_name}</div>
              {boosted && <Badge className="rounded-sm text-[9px] bg-amber-500 text-white"><Sparkles size={8} className="mr-0.5" /> Featured</Badge>}
              {live && !boosted && <Badge className="rounded-sm text-[9px] bg-emerald-600 text-white animate-pulse">Live</Badge>}
            </div>
            <div className="text-[11px] text-gray-500 truncate mt-0.5">
              {b.cuisine || (b.mobile_type === "food_truck" ? "Food truck" : b.mobile_type === "popup" ? "Pop-up shop" : "Mobile business")}
              {b.city ? ` · ${b.city}${b.state ? `, ${b.state}` : ""}` : ""}
              {b._dist != null && Number.isFinite(b._dist) && ` · ${b._dist.toFixed(1)}mi`}
            </div>
          </div>
        </div>

        {live && loc && (
          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs">
            <div className="font-semibold text-emerald-800 flex items-center gap-1"><MapPin size={11} /> Here now</div>
            <div className="text-emerald-900 mt-0.5 truncate">{loc.label}</div>
            {loc.pinged_at && <div className="text-[10px] text-emerald-700 font-mono mt-0.5">Pinged {relativeTime(loc.pinged_at)}</div>}
          </div>
        )}

        <div className="mt-3 flex gap-2 flex-wrap">
          <Link to={`/business/${b.id}`}>
            <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" data-testid={`vendor-view-${b.id}`}>View profile</Button>
          </Link>
          {directions && (
            <a href={directions} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="rounded-sm h-8 text-xs bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid={`vendor-directions-${b.id}`}>
                <ExternalLink size={11} className="mr-1" /> Directions
              </Button>
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}

function haversineMi(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3959; // miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function relativeTime(iso) {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
